import { Button } from '@src/common/components/button/Button';
import { Checkbox } from '@src/common/components/Checkbox';
import { FormTextInput } from '@src/common/components/FormTextInput';
import { Stack } from '@src/common/components/Stack';
import { Typography } from '@src/common/components/Typography';
import { theme } from '@src/common/styles/Theme';
import type { CollapseListener } from '@src/orders/CollapseListener';
import type { OrderPermitApplication } from '@src/orders/domain/Order';
import { EditOrderCollapsible } from '@src/orders/EditOrderCollapsible';
import type { FormEntries } from '@src/orders/FormEntries';
import { useSectionError } from '@src/orders/hooks/useSectionError';
import { ConditionalField } from '@src/orders/PermitFormSections/ConditionalField';
import { ControllerFormTextInput } from '@src/orders/PermitFormSections/ControllerFormTextInput';
import { FormDatePicker } from '@src/orders/PermitFormSections/FormDatePicker';
import { FormDateTimePicker } from '@src/orders/PermitFormSections/FormDateTimePicker';
import { FormWeightInput } from '@src/orders/PermitFormSections/FormWeightInput';
import { useDisabledFields } from '@src/orders/PermitFormSections/useDisabledFields';
import {
  EffectiveDateField,
  EffectiveDateTimeField,
  globalTripFieldNames,
  returnTripFields,
  stateTripFieldNames,
  TripBaseRegisteredWeightField,
  TripDestinationField,
  TripFinalDestinationField,
  TripIncludesReturnTripField,
  TripMileageField,
  TripOriginalOriginField,
  TripOriginField,
  TripReturnDestinationField,
  TripReturnMileageField,
  TripReturnRouteField,
  TripRouteField,
  TripsFormName,
} from '@src/permitField/TripInformationFields';
import type { TFunction } from 'i18next';
import { useCallback, useEffect } from 'react';
import type { Control, UseFormUnregister } from 'react-hook-form';
import { useFormContext, useWatch } from 'react-hook-form';
import type { FieldErrors } from 'react-hook-form/dist/types/errors';
import type { UseFormRegister } from 'react-hook-form/dist/types/form';
import { useTranslation } from 'react-i18next';

const tripFieldNames = stateTripFieldNames();
const globalFieldNames = globalTripFieldNames();

type TripInformation = FormEntries<typeof tripFieldNames>;

type TripsInformation = {
  trips: { [key: string]: TripInformation };
} & FormEntries<typeof globalFieldNames>;

type Props = {
  applications: OrderPermitApplication[];
  fields: string[];
  readonly?: boolean;
} & CollapseListener;

export const TripInformationSection = ({ applications, ...props }: Props) => {
  const { t } = useTranslation();
  const { isFieldDisabled } = useDisabledFields();

  const {
    register,
    unregister,
    control,
    formState: { errors, disabled: formDisabled },
    setValue,
  } = useFormContext<TripsInformation>();

  const { sectionInError } = useSectionError([
    ...tripFieldNames,
    ...globalFieldNames,
  ]);

  if (props.fields.length <= 0) {
    return null;
  }

  const orderHasTripField = props.fields.some((item) =>
    tripFieldNames.includes(item),
  );

  const applicationHasTripField = (application: OrderPermitApplication) =>
    application.application.fields.some((field) =>
      tripFieldNames.includes(field),
    );

  const applyValueToAllFields = (fieldName: string, value: string) => {
    applications.forEach((application) => {
      if (!application.application.hasField(fieldName)) {
        return;
      }

      const fullFieldName = tripInformationSubFormFieldName(
        fieldName,
        application.application.id,
      );
      if (isFieldDisabled(fullFieldName)) {
        return;
      }

      setValue(fullFieldName, value);
    });
  };

  return (
    <EditOrderCollapsible
      title={t('tripInformation')}
      id="tripInformation"
      collapseEventName={props.collapseEventName}
      uncollapseEventName={props.uncollapseEventName}
      inError={sectionInError}
    >
      <Stack gap={theme.spacing['3']}>
        <Stack direction="row" gap={theme.spacing['3']}>
          <ConditionalField
            fields={props.fields}
            conditions={TripOriginalOriginField.fieldName}
          >
            <FormTextInput
              register={register}
              errors={errors}
              fieldName={TripOriginalOriginField.fieldName}
              disabled={
                formDisabled ||
                isFieldDisabled(TripOriginalOriginField.fieldName)
              }
            />
          </ConditionalField>
          <ConditionalField
            fields={props.fields}
            conditions={TripFinalDestinationField.fieldName}
          >
            <FormTextInput
              register={register}
              errors={errors}
              fieldName={TripFinalDestinationField.fieldName}
              disabled={
                formDisabled ||
                isFieldDisabled(TripFinalDestinationField.fieldName)
              }
            />
          </ConditionalField>
        </Stack>

        {orderHasTripField &&
          applications.map(
            (application) =>
              applicationHasTripField(application) && (
                <PermitTripInformationSection
                  key={`specificTripInformationSection-${application.application.id}`}
                  application={application}
                  register={register}
                  unregister={unregister}
                  control={control as unknown as Control}
                  errors={
                    errors &&
                    errors[TripsFormName] &&
                    errors[TripsFormName][application.application.id]
                  }
                  onApplyToAll={applyValueToAllFields}
                  readonly={props.readonly}
                />
              ),
          )}
      </Stack>
    </EditOrderCollapsible>
  );
};

interface TripSubSectionProps {
  application: OrderPermitApplication;
  control: Control;
  register: UseFormRegister<TripsInformation>;
  unregister: UseFormUnregister<TripsInformation>;
  errors: FieldErrors<TripInformation> | undefined;
  onApplyToAll: (fieldName: string, value: string) => void;
  readonly?: boolean;
}

const PermitTripInformationSection = ({
  application,
  control,
  register,
  unregister,
  errors,
  onApplyToAll,
  readonly,
}: TripSubSectionProps) => {
  const { t } = useTranslation();
  const { isFieldDisabled } = useDisabledFields();
  const [tripIncludesReturn, baseRegisteredWeight, effectiveDate] = useWatch({
    control,
    name: [
      tripInformationSubFormFieldName(
        TripIncludesReturnTripField.fieldName,
        application.application.id,
      ),
      tripInformationSubFormFieldName(
        TripBaseRegisteredWeightField.fieldName,
        application.application.id,
      ),
      tripInformationSubFormFieldName(
        EffectiveDateField.fieldName,
        application.application.id,
      ),
    ],
  });

  // Check if a trip field is disabled by checking the full form field name
  const isTripFieldDisabled = (fieldName: string) => {
    return isFieldDisabled(
      tripInformationSubFormFieldName(fieldName, application.application.id),
    );
  };

  const unregisterReturnTripFields = useCallback(() => {
    if (!tripIncludesReturn) {
      Object.keys(returnTripFields).forEach((returnField) => {
        if (returnField === TripIncludesReturnTripField.fieldName) {
          return;
        }
        unregister(
          tripInformationSubFormFieldName(
            returnField,
            application.application.id,
          ),
        );
      });
    }
  }, [tripIncludesReturn, unregister, application.application.id]);
  useEffect(() => {
    unregisterReturnTripFields();
  }, [unregisterReturnTripFields]);
  return (
    <Stack
      aria-labelledby={`specificTripInformationSection-${application.application.id}`}
      role="region"
      gap={theme.spacing['3']}
    >
      <Typography
        id={`specificTripInformationSection-${application.application.id}`}
        component="h3"
        variant="h6"
      >
        {`${application.permitTypeJurisdiction}: ${application.permitTypeName}`}
      </Typography>

      <Stack direction="row" gap={theme.spacing['3']}>
        <ConditionalField
          fields={application.application.fields}
          conditions={TripOriginField.fieldName}
        >
          <ControllerFormTextInput
            control={control}
            label={t(`fields.${TripOriginField.fieldName}`)}
            fieldName={tripInformationSubFormFieldName(
              TripOriginField.fieldName,
              application.application.id,
            )}
            disabled={
              readonly || isTripFieldDisabled(TripOriginField.fieldName)
            }
          />
        </ConditionalField>

        <ConditionalField
          fields={application.application.fields}
          conditions={TripDestinationField.fieldName}
        >
          <ControllerFormTextInput
            control={control}
            label={t(`fields.${TripDestinationField.fieldName}`)}
            fieldName={tripInformationSubFormFieldName(
              TripDestinationField.fieldName,
              application.application.id,
            )}
            disabled={
              readonly || isTripFieldDisabled(TripDestinationField.fieldName)
            }
          />
        </ConditionalField>

        <ConditionalField
          fields={application.application.fields}
          conditions={TripBaseRegisteredWeightField.fieldName}
        >
          <FormWeightInput
            {...customMetricInputProps({
              t,
              fieldName: TripBaseRegisteredWeightField.fieldName,
              control: control as Control,
              errors,
              applicationId: application.application.id,
            })}
            disabled={
              readonly ||
              isTripFieldDisabled(TripBaseRegisteredWeightField.fieldName)
            }
            endAdornment={
              !readonly &&
              !isTripFieldDisabled(TripBaseRegisteredWeightField.fieldName) ? (
                <ApplyAllButton
                  onApplyToAll={() => {
                    onApplyToAll(
                      TripBaseRegisteredWeightField.fieldName,
                      baseRegisteredWeight,
                    );
                  }}
                />
              ) : undefined
            }
          />
        </ConditionalField>
      </Stack>
      <Stack direction="row" gap={theme.spacing['3']}>
        <ConditionalField
          fields={application.application.fields}
          conditions={TripRouteField.fieldName}
        >
          <ControllerFormTextInput
            control={control}
            label={t(`fields.${TripRouteField.fieldName}`)}
            fieldName={tripInformationSubFormFieldName(
              TripRouteField.fieldName,
              application.application.id,
            )}
            multiline
            disabled={readonly || isTripFieldDisabled(TripRouteField.fieldName)}
          />
        </ConditionalField>

        <ConditionalField
          fields={application.application.fields}
          conditions={TripMileageField.fieldName}
        >
          <ControllerFormTextInput
            control={control}
            label={t(`fields.${TripMileageField.fieldName}`)}
            fieldName={tripInformationSubFormFieldName(
              TripMileageField.fieldName,
              application.application.id,
            )}
            type="number"
            disabled={
              readonly || isTripFieldDisabled(TripMileageField.fieldName)
            }
          />
        </ConditionalField>

        <ConditionalField
          conditions={[
            EffectiveDateField.fieldName,
            EffectiveDateTimeField.fieldName,
          ]}
          fields={application.application.fields}
        >
          {application.application.hasField(
            EffectiveDateTimeField.fieldName,
          ) ? (
            <FormDateTimePicker
              {...customDatePickerProps({
                t,
                fieldName: EffectiveDateField.fieldName,
                control,
                errors,
                applicationId: application.application.id,
              })}
              disabled={
                readonly || isTripFieldDisabled(EffectiveDateField.fieldName)
              }
              endAdornment={
                !readonly &&
                !isTripFieldDisabled(EffectiveDateField.fieldName) ? (
                  <ApplyAllButton
                    onApplyToAll={() => {
                      onApplyToAll(EffectiveDateField.fieldName, effectiveDate);
                    }}
                  />
                ) : undefined
              }
              isUTC
            />
          ) : (
            <FormDatePicker
              {...customDatePickerProps({
                t,
                fieldName: EffectiveDateField.fieldName,
                control,
                errors,
                applicationId: application.application.id,
              })}
              disabled={
                readonly || isTripFieldDisabled(EffectiveDateField.fieldName)
              }
              endAdornment={
                !readonly &&
                !isTripFieldDisabled(EffectiveDateField.fieldName) ? (
                  <ApplyAllButton
                    onApplyToAll={() => {
                      onApplyToAll(EffectiveDateField.fieldName, effectiveDate);
                    }}
                  />
                ) : undefined
              }
              isUTC
            />
          )}
        </ConditionalField>
      </Stack>

      <ConditionalField
        fields={application.application.fields}
        conditions={TripIncludesReturnTripField.fieldName}
      >
        <CustomCheckbox
          errors={errors}
          fieldName={TripIncludesReturnTripField.fieldName}
          register={register}
          applicationId={application.application.id}
          checked={!!tripIncludesReturn}
          disabled={
            readonly ||
            isTripFieldDisabled(TripIncludesReturnTripField.fieldName)
          }
        />
      </ConditionalField>

      {tripIncludesReturn && (
        <Stack direction="row" gap={theme.spacing['3']}>
          <ConditionalField
            fields={application.application.fields}
            conditions={TripReturnDestinationField.fieldName}
          >
            <ControllerFormTextInput
              control={control}
              label={t(`fields.${TripReturnDestinationField.fieldName}`)}
              fieldName={tripInformationSubFormFieldName(
                TripReturnDestinationField.fieldName,
                application.application.id,
              )}
              disabled={
                readonly ||
                isTripFieldDisabled(TripReturnDestinationField.fieldName)
              }
            />
          </ConditionalField>

          <ConditionalField
            fields={application.application.fields}
            conditions={TripReturnRouteField.fieldName}
          >
            <ControllerFormTextInput
              control={control}
              label={t(`fields.${TripReturnRouteField.fieldName}`)}
              fieldName={tripInformationSubFormFieldName(
                TripReturnRouteField.fieldName,
                application.application.id,
              )}
              multiline
              disabled={
                readonly || isTripFieldDisabled(TripReturnRouteField.fieldName)
              }
            />
          </ConditionalField>

          <ConditionalField
            fields={application.application.fields}
            conditions={TripReturnMileageField.fieldName}
          >
            <ControllerFormTextInput
              control={control}
              label={t(`fields.${TripReturnMileageField.fieldName}`)}
              fieldName={tripInformationSubFormFieldName(
                TripReturnMileageField.fieldName,
                application.application.id,
              )}
              type="number"
              disabled={
                readonly ||
                isTripFieldDisabled(TripReturnMileageField.fieldName)
              }
            />
          </ConditionalField>
        </Stack>
      )}
    </Stack>
  );
};

const customMetricInputProps = ({
  t,
  control,
  fieldName,
  errors,
  applicationId,
  isNumerical,
}: {
  t: TFunction;
  control: Control;
  fieldName: string;
  errors: FieldErrors<TripInformation> | undefined;
  applicationId: string;
  isNumerical?: boolean;
}) => ({
  label: t(`fields.${fieldName}`),
  fieldName,
  error:
    errors &&
    errors[fieldName] &&
    t(`formErrors.${errors[fieldName]!.message}`),
  type: isNumerical ? 'number' : 'text',
  name: tripInformationSubFormFieldName(fieldName, applicationId),
  control,
});

const customDatePickerProps = ({
  t,
  control,
  fieldName,
  errors,
  applicationId,
}: {
  t: TFunction;
  control: Control;
  fieldName: string;
  errors: FieldErrors<TripInformation> | undefined;
  applicationId: string;
}) => {
  return {
    label: t(`fields.${fieldName}`),
    fieldName: tripInformationSubFormFieldName(fieldName, applicationId),
    error:
      errors &&
      errors[fieldName] &&
      t(`formErrors.${errors[fieldName]!.message}`),
    name: tripInformationSubFormFieldName(fieldName, applicationId),
    control,
  };
};

const CustomCheckbox = ({
  register,
  fieldName,
  errors,
  applicationId,
  checked,
  disabled,
}: {
  register: UseFormRegister<TripsInformation>;
  fieldName: string;
  errors: FieldErrors<TripInformation> | undefined;
  applicationId: string;
  checked?: boolean;
  disabled?: boolean;
}) => {
  const { t } = useTranslation();

  return (
    <Checkbox
      {...register(tripInformationSubFormFieldName(fieldName, applicationId))}
      label={t(`fields.${fieldName}`)}
      checked={checked}
      disabled={disabled}
      error={
        errors &&
        errors[fieldName] &&
        t(`formErrors.${errors[fieldName]!.message}`)
      }
    />
  );
};

const tripInformationSubFormFieldName = (
  formFieldName: string,
  applicationId: string,
): 'trips' | `trips.${string}` | `trips.${string}.${string}` => {
  return `${TripsFormName}.${applicationId}.${formFieldName}`;
};

const ApplyAllButton = ({ onApplyToAll }: { onApplyToAll: () => void }) => {
  const { t } = useTranslation();
  return (
    <Button style={{ whiteSpace: 'nowrap' }} onClick={onApplyToAll}>
      {t('applyAll')}
    </Button>
  );
};
